import { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { FACTORY_ADDRESS, FACTORY_ABI, VAULT_ABI, ERC20_ABI } from "../constants";

export interface VaultInfo {
  address: string;
  name: string;
  lpTokenAddress: string;
  token0: string;
  token1: string;
  totalLpManaged: string;
  pricePerShare: string;
  pendingRewards: string;
  perfFeeBps: number;
  withdrawFeeBps: number;
  lastHarvest: Date | null;
  totalFeesCollected: string;
  totalRewardsCompounded: string;
  isStable: boolean;
  apr: number | null;
  aprCompounded: number | null;
  poolTvlUsd: number | null;   // DefiLlama pool-level TVL in USD
  userShares: string;
  userLpValue: string;
  userLpBalance: string;
  lpAllowance: string;
}

interface LlamaPool {
  project: string;
  chain: string;
  apy: number | null;
  apyReward: number | null;
  tvlUsd: number | null;
  underlyingTokens: string[] | null;
}

async function fetchLlamaAprs(): Promise<{ aprMap: Map<string, number>; tvlMap: Map<string, number> }> {
  try {
    const res = await fetch("https://yields.llama.fi/pools");
    if (!res.ok) return { aprMap: new Map(), tvlMap: new Map() };
    const { data }: { data: LlamaPool[] } = await res.json();
    // Map of key -> {apy, isV1} — prefer aerodrome-v1, cap at 500%
    const map = new Map<string, number>();
    const tvlMap = new Map<string, number>();
    const v1Keys = new Set<string>();
    for (const pool of data) {
      if (pool.project !== "aerodrome-v1" && pool.project !== "aerodrome") continue;
      if (pool.chain !== "Base") continue;
      if (!pool.underlyingTokens || pool.underlyingTokens.length < 2) continue;
      const apy = pool.apy ?? (pool.apyReward ?? 0);
      if (!apy || apy <= 0 || apy > 500) continue; // cap at 500%
      const key = pool.underlyingTokens.map((t: string) => t.toLowerCase()).sort().join(":");
      const isV1 = pool.project === "aerodrome-v1";
      // Prefer aerodrome-v1 entries; within same project keep highest
      if (!map.has(key)) {
        map.set(key, apy);
        if (pool.tvlUsd) tvlMap.set(key, pool.tvlUsd);
        if (isV1) v1Keys.add(key);
      } else if (isV1 && !v1Keys.has(key)) {
        // Upgrade to v1 entry
        map.set(key, apy);
        if (pool.tvlUsd) tvlMap.set(key, pool.tvlUsd);
        v1Keys.add(key);
      } else if (isV1 === v1Keys.has(key) && map.get(key)! < apy) {
        // Same tier, keep higher
        map.set(key, apy);
        if (pool.tvlUsd) tvlMap.set(key, pool.tvlUsd);
      }
    }
    return { aprMap: map, tvlMap };
  } catch {
    return { aprMap: new Map(), tvlMap: new Map() };
  }
}

function compoundApr(baseAprPct: number): number {
  const r = baseAprPct / 100;
  const n = 24 * 365;
  return ((1 + r / n) ** n - 1) * 100;
}

export function useVaults(
  userAddress: string | null,
  provider: ethers.BrowserProvider | null
) {
  const [vaults, setVaults]   = useState<VaultInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!provider) return;
    setLoading(true);
    setError(null);
    try {
      const [llamaData, factory] = await Promise.all([
        fetchLlamaAprs(),
        Promise.resolve(new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, provider)),
      ]);
      const vaultAddrs: string[] = await factory.getAllVaults();
      const { aprMap, tvlMap } = llamaData;
      const vaultData = await Promise.all(
        vaultAddrs.map(async (vaultAddr): Promise<VaultInfo> => {
          const vault = new ethers.Contract(vaultAddr, VAULT_ABI, provider);
          const [
            name, totalLpManaged, pricePerShare, pendingRewards,
            perfFeeBps, withdrawFeeBps, lastHarvestTs, lpAddr,
            totalFeesCollected, totalRewardsCompounded, isStable,
            token0, token1,
          ] = await Promise.all([
            vault.vaultName(), vault.totalLpManaged(), vault.pricePerShare(),
            vault.pendingRewards(), vault.perfFeeBps(), vault.withdrawFeeBps(),
            vault.lastHarvestTimestamp(), vault.lpToken(), vault.totalFeesCollected(),
            vault.totalRewardsCompounded(), vault.isStable(), vault.token0(), vault.token1(),
          ]);
          const aprKey = [token0.toLowerCase(), token1.toLowerCase()].sort().join(":");
          const baseApr = aprMap.get(aprKey) ?? null;
          const poolTvlUsd = tvlMap.get(aprKey) ?? null;
          const aprComp = baseApr !== null ? compoundApr(baseApr) : null;
          let userShares = 0n, userLpValue = 0n, userLpBal = 0n, lpAllowance = 0n;
          if (userAddress) {
            const lp = new ethers.Contract(lpAddr, ERC20_ABI, provider);
            [userShares, userLpBal, lpAllowance] = await Promise.all([
              vault.balanceOf(userAddress), lp.balanceOf(userAddress),
              lp.allowance(userAddress, vaultAddr),
            ]);
            if (userShares > 0n) userLpValue = await vault.getLpForShares(userShares);
          }
          return {
            address: vaultAddr, name, lpTokenAddress: lpAddr, token0, token1,
            totalLpManaged: ethers.formatEther(totalLpManaged),
            pricePerShare: ethers.formatEther(pricePerShare),
            pendingRewards: ethers.formatEther(pendingRewards),
            perfFeeBps: Number(perfFeeBps), withdrawFeeBps: Number(withdrawFeeBps),
            lastHarvest: lastHarvestTs > 0n ? new Date(Number(lastHarvestTs) * 1000) : null,
            totalFeesCollected: ethers.formatEther(totalFeesCollected),
            totalRewardsCompounded: ethers.formatEther(totalRewardsCompounded),
            isStable: Boolean(isStable), apr: baseApr, aprCompounded: aprComp,
            poolTvlUsd,
            userShares: ethers.formatEther(userShares),
            userLpValue: ethers.formatEther(userLpValue),
            userLpBalance: ethers.formatEther(userLpBal),
            lpAllowance: ethers.formatEther(lpAllowance),
          };
        })
      );
      setVaults(vaultData);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load vaults");
    } finally {
      setLoading(false);
    }
  }, [provider, userAddress]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  return { vaults, loading, error, refresh };
}